/* eslint-disable react/no-children-prop */
import React, { useState } from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { env } from 'env';
import {
  GridColDef,
  TextWithIndicator,
  countryCodesMap,
  Tenant,
  useRightSideBar
} from '@p81-common/ui';
import { useRegionalLink } from 'hooks/useRegionalLink';

import CLMSideBar from '../RightSideBarComp/CMLSideBar';
import createClasses from '../RightSideBarComp/styles';
import { access, hasAccess, resources } from 'utils/permissions';
import { releaseCloudJob, retryCloudJob } from 'utils/cloudActions';
import {
  formatElapsed,
  getElapsedMs,
  isFailedCMStatus,
  isInProgressCMStatus,
  isRetryableScope
} from 'utils/cloudManagement';
import JobActionConfirmModal from '../parts/JobActionConfirmModal/JobActionConfirmModal';
import type {
  JobActionKind,
  PendingJobAction
} from '../parts/JobActionConfirmModal/JobActionConfirmModal';

const JOB_ACTIONS: Record<
  JobActionKind,
  (payload: { customerId: string; networkId: string }) => Promise<boolean>
> = {
  release: releaseCloudJob,
  retry: retryCloudJob
};

/**
 * @param onJobChanged - called after a release or retry succeeds, so the caller
 *  can refresh the list rather than leave a row showing state that no longer
 *  exists.
 * @returns tableConfiguration plus `confirmModal`, which the caller must render.
 *  This is a hook, so it cannot render the modal itself, and the grid's action
 *  config has no slot for JSX.
 */
export const useTableSettings = (onJobChanged?: () => void) => {
  const { t } = useTranslation(['activity']);
  const styles = createClasses({});
  const { url } = useRouteMatch();
  const { setComponentSecondaryDrawer, setOpenSecondary } = useRightSideBar();
  const generateRegionalPath = useRegionalLink();

  /*
   * Force-release and retry are irreversible and act on a customer's production
   * network, so the row action only *stages* one — the request is issued from the
   * modal's confirm. `isRunning` then disables both buttons while it is in
   * flight: without it a second click fires a second release or retry, and for
   * retry that is a second real action dispatched at the platform.
   */
  const [pendingAction, setPendingAction] = useState<PendingJobAction | null>(
    null
  );
  const [isRunning, setIsRunning] = useState(false);

  /*
   * The grid types row fields as optional, and a job is addressed by the pair —
   * a row missing either id cannot identify one, so there is nothing meaningful
   * to send. Narrowing here keeps both ids as plain strings at the call sites.
   */
  const stageJobAction =
    (
      row: {
        customerId?: string;
        networkId?: string;
        networkName?: string;
        action?: string;
      },
      kind: JobActionKind
    ) =>
    () => {
      const { customerId, networkId } = row;
      if (!customerId || !networkId) return;

      setPendingAction({
        kind,
        customerId,
        networkId,
        networkName: row.networkName,
        action: row.action
      });
    };

  const confirmJobAction = async () => {
    if (!pendingAction || isRunning) return;

    const { kind, customerId, networkId } = pendingAction;

    setIsRunning(true);
    try {
      // releaseCloudJob/retryCloudJob own their own success and error toasts, so
      // nothing here should notify as well.
      const ok = await JOB_ACTIONS[kind]({ customerId, networkId });
      if (ok) onJobChanged?.();
    } finally {
      setIsRunning(false);
      setPendingAction(null);
    }
  };

  const confirmModal = (
    <JobActionConfirmModal
      open={Boolean(pendingAction)}
      pending={pendingAction}
      isLoading={isRunning}
      onConfirm={confirmJobAction}
      onClose={() => {
        if (!isRunning) setPendingAction(null);
      }}
    />
  );

  const tableConfiguration: GridColDef[] = [
    {
      show: true,
      isSortable: true,
      field: 'datetime',
      title: 'Created Date',
      width: 170,
      type: 'dateCreated',
      fieldsToExport: ['datetime']
    },
    {
      isSortable: true,
      field: 'action',
      title: 'Action',
      show: true,
      width: 180,
      fieldsToExport: ['action'],
      type: 'textList',
      renderCell(params) {
        const { row } = params;
        return (
          <Typography
            fontSize={14}
            children={t(
              `activity:cm.ACTION-${row?.action?.trim().toUpperCase()}`
            )}
          />
        );
      }
    },
    {
      isSortable: true,
      field: 'status',
      title: 'Status',
      show: true,
      width: 181,
      fieldsToExport: ['status'],
      type: 'indicatedText',
      renderCell: params => {
        const { row } = params;
        if (!row.status) return '-';
        const statusValue: string = ['inprogress', 'pending'].includes(
          row.status
        )
          ? `${row.status === 'inprogress' ? 'in progress' : row.status} ${
              row.progress ? row.progress : ''
            }`
          : row.status;
        const indicatorColors = {
          failed: '#FD596F',
          success: '#44D7B6',
          inProgress: '#266FE2'
        };
        const color = statusValue.includes('success')
          ? indicatorColors.success
          : statusValue.includes('failed')
          ? indicatorColors.failed
          : indicatorColors.inProgress;

        /*
         * Age matters most while an action is unfinished: without it a job stuck
         * for a day looks exactly like one started a moment ago, which is why the
         * P81-143028 restart sat unnoticed until the customer complained.
         */
        const elapsed = isInProgressCMStatus(row.status)
          ? formatElapsed(getElapsedMs(row.datetime))
          : '';

        return (
          <TextWithIndicator
            className={styles.capitalize}
            children={elapsed ? `${statusValue} · ${elapsed}` : statusValue}
            indicatorColor={color}
            indicatorShape="circle"
          />
        );
      }
    },
    {
      isSortable: true,
      field: 'networkName',
      title: 'Network',
      show: true,
      width: 155,
      fieldsToExport: ['networkName']
    },
    {
      isSortable: true,
      field: 'networkId',
      title: 'Network ID',
      show: true,
      width: 130,
      fieldsToExport: ['networkId'],
      renderCell: params => {
        const { row } = params;
        const hasTenantSensitiveDataReadAccess = hasAccess([
          resources.TENANT_SENSITIVE_DATA,
          access.READ
        ]);
        return hasTenantSensitiveDataReadAccess ? (
          <NavLink
            to={generateRegionalPath(
              `${url}/${row?.customerId}/platform/networks`
            )}
          >
            <Typography className={styles.linkColumn} fontSize={14}>
              {row.networkId}
            </Typography>
          </NavLink>
        ) : (
          row.networkId
        );
      }
    },
    {
      isSortable: true,
      field: 'entities.gatewayId',
      title: 'Gateway ID',
      show: true,
      width: 150,
      fieldsToExport: ['entities'],
      renderCell(params) {
        const { row } = params;
        if (!row.entities) return <Typography children={'-'} />;
        return (
          <Typography
            fontSize={14}
            children={row?.entities[0]?.gatewayId || '-'}
          />
        );
      }
    },
    {
      isSortable: true,
      field: 'entities.gatewayImageVersion',
      title: 'Gateway Version',
      show: true,
      width: 170,
      fieldsToExport: ['entities'],
      renderCell(params) {
        const { row } = params;
        if (!row.entities) return <Typography children={'-'} />;
        return (
          <Typography
            fontSize={14}
            children={row?.entities[0]?.gatewayImageVersion || '-'}
          />
        );
      }
    },
    {
      isSortable: false,
      field: 'lastBeacon',
      title: 'Beacon ID',
      show: true,
      width: 140,
      fieldsToExport: ['lastBeacon'],
      renderCell: params => {
        const { row } = params;
        return row?.lastBeacon?.networkBeaconId;
      }
    },
    {
      isSortable: false,
      field: 'batchId',
      title: 'Batch ID',
      show: true,
      width: 140,
      fieldsToExport: ['batchId']
    },
    {
      isSortable: true,
      field: 'entities.tunnelId',
      title: 'Tunnel ID',
      show: true,
      width: 150,
      fieldsToExport: ['entities'],
      renderCell(params) {
        const { row } = params;
        if (!row.entities) return <Typography children={'-'} />;
        return (
          <Typography
            fontSize={14}
            children={row?.entities[0]?.tunnelId || '-'}
          />
        );
      }
    },
    {
      isSortable: true,
      field: 'companyName',
      title: 'Company Name',
      show: true,
      width: 228,
      fieldsToExport: [
        'companyName',
        'customerType',
        'companySize',
        'customerId'
      ],
      renderCell: params => {
        const { row } = params;
        const country = row?.country?.toString();
        //@ts-ignore
        const countryData = countryCodesMap[country];
        const countryFlag = countryData?.flag || 'worldwide';
        const countryFlagSrc = `${env.REACT_APP_STATIC_ASSETS_URL}/flags/${countryFlag}.svg`;
        return (
          <NavLink
            to={generateRegionalPath(
              `${url}/${row.customerId}/platform/networks`
            )}
          >
            <Tenant
              companyIconProps={{
                badgeProps: {
                  badgeContent: (
                    <img
                      alt="flag"
                      height="16px"
                      src={countryFlagSrc}
                      width="16px"
                    />
                  )
                },
                domain: row?.website
              }}
              subTexts={[row?.companySize, row?.customerType]}
              title={row?.companyName}
            />
          </NavLink>
        );
      }
    },
    {
      isSortable: true,
      field: 'updatedByName',
      title: 'Updated By',
      show: true,
      width: 196,
      fieldsToExport: ['updatedByName', 'updatedByEmail'],
      type: 'userName'
    },
    {
      isSortable: true,
      field: 'actions',
      title: '',
      show: true,
      width: 90,
      align: 'right',
      fieldsToExport: [],
      /*
       * None of these set `disableHover`, and the absence is the point: the prop
       * reads backwards — `disableHover: true` means "always visible". Leaving it
       * off is what makes an icon reveal on row hover, which is how the 3-dots has
       * always behaved and now how all three do.
       */
      actions: [
        {
          iconSrc: `${env.REACT_APP_STATIC_ASSETS_URL}/icons/icon_refresh.svg`,
          tooltipText: 'Retry this action',
          value: 'retryJob',
          onClick: (value, row) => stageJobAction(row, 'retry'),
          /*
           * Offered both while the action is in flight — retryJob releases the
           * lock before re-issuing, so it is the escape hatch for a wedged job —
           * and once it has failed, which includes the `failed(no response)` the
           * backstop stamps on jobs it gave up on. Those are exactly the rows an
           * operator wants to re-run. Never offered on a success.
           *
           * Also hidden for gateway- and tunnel-scoped rows, which the API
           * refuses with a 400: a re-issue rebuilt without the narrowing id would
           * target the whole network. Better no button than one that always fails.
           */
          hide: (row: any) =>
            (!isInProgressCMStatus(row.status) &&
              !isFailedCMStatus(row.status)) ||
            !isRetryableScope(row) ||
            !hasAccess([resources.CLOUD_MANAGEMENT, access.WRITE]),
          disabled: () => isRunning
        },
        {
          iconSrc: `${env.REACT_APP_STATIC_ASSETS_URL}/icons/icon_yellow_warning.svg`,
          tooltipText: 'Force-release this stuck job',
          value: 'releaseJob',
          onClick: (value, row) => stageJobAction(row, 'release'),
          // Unlike retry, this is only meaningful while a lock is presumably
          // still held — there is nothing to release on a finished job.
          hide: (row: any) =>
            !isInProgressCMStatus(row.status) ||
            !hasAccess([resources.CLOUD_MANAGEMENT, access.WRITE]),
          disabled: () => isRunning
        },
        {
          iconSrc: `${env.REACT_APP_STATIC_ASSETS_URL}/icons/icon_3dots.svg`,
          value: 'view',
          sx: {
            transform: 'rotate(90deg)'
          },
          onClick: (value, row) => () => {
            setComponentSecondaryDrawer(
              <CLMSideBar url={url} rowId={row._id} />
            );
            setOpenSecondary(true);
          }
        }
      ]
    }
  ];

  return { tableConfiguration, confirmModal };
};
